import React, { useEffect, useRef, useState } from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import { useSelector } from 'react-redux'
import ButtonStartIcon from '../../../../button/ButtonStartIcon'
import List from './List/List'
import ThirdPartyAccessDialog from './Dialog/Dialog'
import HistoryDialog from './History/HistoryDialog'
import OtpDialog from './Otp/OtpDialog'
import useThirdPartyAccess from '../../../../../hooks/setting/useThirdPartyAccess'

const isMaestroRole = (roles = []) => roles.some((r) => r?.id === 'Maestro')

const FULL_PERMISSION = {
  created: true,
  updated: true,
  deleted: true,
  sync: true,
  history: true
}

const ThirdPartyAccess = () => {
  const [type, setType] = useState(null)
  const [form, setForm] = useState(null)
  const [historyItem, setHistoryItem] = useState(null)

  const roles = useSelector((state) => state?.session?.user?.roles || [])
  const isMaestro = isMaestroRole(roles)
  const permObj = useSelector((state) => state?.session?.permissions?.['third-party-access'] || {})
  // Credenciales de servidor sensibles: acceso completo para Maestro; si no,
  // se respeta el permiso granular que exista.
  const permission = isMaestro ? FULL_PERMISSION : permObj

  const list = useSelector((state) => state?.tenant?.thirdPartyAccess || [])
  const { isLoadingList, sync, submitCode, refresh } = useThirdPartyAccess()

  // MF-594 — ACER may ask for a code by email: after a sync starts we look at how it goes for a while and, if it
  // is waiting for the code, open the dialog to type it.
  const [otpItem, setOtpItem] = useState(null)
  const mounted = useRef(true)
  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
    }
  }, [])

  // Looks at a sync after it starts: opens the code dialog if ACER asks for it, and keeps the list up to date while the
  // sync runs (a school like Belmont takes minutes) until it finishes. No time limit: it only stops when the sync ends
  // or the screen is left.
  const watchSync = async (id, since) => {
    const WAIT_FOR_CODE_POLLS = 12 // 12 x 2.5 s: how long the login can take before it asks for the code
    for (let i = 0; mounted.current; i++) {
      await new Promise((resolve) => setTimeout(resolve, i < WAIT_FOR_CODE_POLLS ? 2500 : 5000))
      if (!mounted.current) return
      const items = await refresh({ silent: true })
      const item = items.find((x) => x._id === id)
      if (!item) return
      if (item.lastSyncStatus === 'awaiting-code') {
        setOtpItem(item)
        return
      }
      const finished =
        ['ok', 'error'].includes(item.lastSyncStatus) && new Date(item.lastSyncAt).getTime() >= since
      if (finished) return
      // Still logging in or no news after the first minute and nothing is running: there is nothing to wait for
      if (i >= WAIT_FOR_CODE_POLLS && item.lastSyncStatus !== 'running') return
    }
  }

  const onSync = async (id) => {
    const since = Date.now()
    const result = await sync(id)
    if (!result.error) watchSync(id, since)
    return result
  }

  const onSubmitCode = async (code, rememberDevice) => {
    const id = otpItem._id
    const result = await submitCode(id, code, rememberDevice)
    if (!result.error) {
      setOtpItem(null)
      refresh({ silent: true })
      watchSync(id, Date.now() - 2000)
    }
    return result
  }

  const onModal = (nextType = null, nextForm = {}) => {
    setType(nextType)
    setForm(nextForm)
  }

  return (
    <>
      <Box className='bg-primary text-primary-foreground px-4 py-1 flex gap-4 items-center justify-between sticky top-0 z-10 h-8'>
        <Typography variant='subtitle1' className='uppercase'>
          Third party access
        </Typography>
        {!isLoadingList && permission['created'] && (
          <ButtonStartIcon
            onClick={() => onModal('create', {})}
            color='secondary'
            variant='contained'
            startIcon={['fas', 'plus']}
          >
            Add credential
          </ButtonStartIcon>
        )}
      </Box>
      <Box className='px-4 pt-3'>
        <Typography variant='caption' color='text.secondary'>
          Store the school&apos;s credentials for an external assessment provider (e.g. ACER OARS / PAT).
          Passwords are encrypted at rest and write-only — they are never shown again. Use <b>Sync</b> to pull
          the latest results. If ACER asks for a verification code, it is sent to the email of the account
          profile and you type it here.
        </Typography>
      </Box>
      <Box className='px-2 py-2'>
        <List
          isLoading={isLoadingList}
          list={list}
          permissions={permission}
          onModal={(t, f) => onModal(t, f)}
          onSync={onSync}
          onEnterCode={(item) => setOtpItem(item)}
          onHistory={(item) => setHistoryItem(item)}
        />
      </Box>
      {type && (
        <ThirdPartyAccessDialog
          type={type}
          show={!!type}
          onClose={() => onModal(null, {})}
          data={form || {}}
        />
      )}
      {otpItem && (
        <OtpDialog
          show={!!otpItem}
          credential={otpItem}
          onClose={() => setOtpItem(null)}
          onSubmit={onSubmitCode}
        />
      )}
      {historyItem && (
        <HistoryDialog show={!!historyItem} credential={historyItem} onClose={() => setHistoryItem(null)} />
      )}
    </>
  )
}

export default ThirdPartyAccess
